import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Pages
import { LandingPage } from "@/pages/LandingPage";
import { AuthPage } from "@/pages/AuthPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { CreateTournamentPage } from "@/pages/CreateTournamentPage";
import { TournamentPage } from "@/pages/TournamentPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { AdminMembersPage } from "@/pages/AdminMembersPage";
import { AdminSponsorsPage } from "@/pages/AdminSponsorsPage";
import { SchedulePage } from "@/pages/SchedulePage";
import { ToursPage } from "@/pages/ToursPage";
import { TourDetailPage } from "@/pages/TourDetailPage";
import { RankingsPage } from "@/pages/RankingsPage";
import { AdminSeasonsPage } from "@/pages/AdminSeasonsPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import SpectatePage from "@/pages/SpectatePage";
import { VenueRegisterPage } from "@/pages/VenueRegisterPage";
import { AdminApprovalsPage } from "@/pages/AdminApprovalsPage";
import { PersonalEventsPage } from "@/pages/PersonalEventsPage";
import JoinPersonalEventPage from "@/pages/JoinPersonalEventPage";
import DrinkTicketPage from "@/pages/DrinkTicketPage";

// Context
import { AuthProvider, useAuth } from "@/context/AuthContext";

// PWA Components
import { MobileNav } from "@/components/MobileNav";
import { InstallPrompt } from "@/components/InstallPrompt";

const ProtectedRoute = ({ children }) => {
  const { member, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="animate-pulse text-primary text-xl font-heading">Loading...</div>
      </div>
    );
  }
  
  if (!member) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/tournaments/create" element={
        <ProtectedRoute>
          <CreateTournamentPage />
        </ProtectedRoute>
      } />
      <Route path="/tournaments/:id" element={
        <ProtectedRoute>
          <TournamentPage />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/admin/members" element={
        <ProtectedRoute>
          <AdminMembersPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/sponsors" element={
        <ProtectedRoute>
          <AdminSponsorsPage />
        </ProtectedRoute>
      } />
      <Route path="/schedule" element={
        <ProtectedRoute>
          <SchedulePage />
        </ProtectedRoute>
      } />
      <Route path="/tours" element={
        <ProtectedRoute>
          <ToursPage />
        </ProtectedRoute>
      } />
      <Route path="/tours/:id" element={
        <ProtectedRoute>
          <TourDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/rankings" element={
        <ProtectedRoute>
          <RankingsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/seasons" element={
        <ProtectedRoute>
          <AdminSeasonsPage />
        </ProtectedRoute>
      } />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/spectate/:id" element={<SpectatePage />} />
      <Route path="/venues/register" element={<VenueRegisterPage />} />
      <Route path="/admin/approvals" element={
        <ProtectedRoute>
          <AdminApprovalsPage />
        </ProtectedRoute>
      } />
      <Route path="/personal-events" element={
        <ProtectedRoute>
          <PersonalEventsPage />
        </ProtectedRoute>
      } />
      <Route path="/join/:code" element={<JoinPersonalEventPage />} />
      <Route path="/tickets/:id" element={
        <ProtectedRoute>
          <DrinkTicketPage />
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <MobileNav />
          <InstallPrompt />
          <Toaster 
            position="top-right" 
            toastOptions={{
              style: {
                background: '#0A0A0A',
                border: '1px solid #27272A',
                color: '#FFFFFF',
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
